import { Badge, IconButton, Stack, FormControlLabel, Switch, Tooltip } from "@mui/material";
import { DeleteForever, Visibility, VisibilityOff, AcUnit } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import {
  ConfirmationModal,
  EContractState,
  useConfirmationModal,
  useToast,
  Dayjs,
  useModal,
} from "@synota-io/synota-shared-ui";
import { useContractLinks } from "../hooks/useContractLinks";
import { useContractHide } from "../../../../api/hooks/useContractHide";
import { IContractDetailsResponse } from "../../../../api/hooks/useContractDetails";
import { useContractDelete } from "../../../../api/hooks/useContractDelete";
import { useFrozenStatusMutations } from "../../../../api/hooks/useFrozenStatusMutations";
import { useFrozenStatus } from "../../../../api/hooks/useFrozenStatus";
import { CONTRACTS_PATH } from "../../../../paths";
import { useNavigateWithSearchState } from "../../../../shared/hooks/useNavigateWithSearchState";
import { FreezeContractModal } from "../forms/FreezeContractModal";
import type { ComponentProps } from "react";
import { useContractMinerManagementVisibility } from "../../../../api/hooks/useContractMinerManagementVisibility";
import { LastEnergyDateEditor } from "../forms/LastEnergyDateEditor";
import { useSelf } from "../../../../api/hooks/useSelf";

export const ContractAdminActions = () => {
  const { self } = useSelf();
  const toast = useToast();
  const navigate = useNavigateWithSearchState();
  const queryClient = useQueryClient();
  const { id, contract, queryKey, isFetching, refetch } = useContractLinks();
  const { setVisibility, isPending: isPendingVisibility } = useContractMinerManagementVisibility();
  const { setHidden, isPending: isPendingHide } = useContractHide();
  const { changeFrozenStatus, isPending: isPendingFreeze } = useFrozenStatusMutations();
  const { deleteContract, isPending: isPendingDelete } = useContractDelete();
  const confirmationModal = useConfirmationModal();
  const freezeModal = useModal();

  const frozenInput = contract
    ? {
        user_email: contract.consumer_email,
        contract_shared_uuid: contract.shared_uuid,
      }
    : undefined;

  const {
    frozenUntil,
    isFetching: isFetchingFrozen,
    refetch: refetchFrozen,
    queryKey: frozenQueryKey,
  } = useFrozenStatus(frozenInput);

  const isFrozen = !!frozenUntil;

  const onChangeHidden = (hidden: boolean) => {
    if (!contract) {
      return;
    }
    confirmationModal.onOpen({
      title: hidden ? "Hide this contract?" : "Make this contract visible?",
      acceptLabel: "Confirm",
      onConfirm: () => {
        setHidden(
          {
            hidden,
            contract_shared_uuid: contract.shared_uuid,
          },
          {
            onSuccess() {
              queryClient.setQueryData<AxiosResponse<IContractDetailsResponse>>(queryKey, (r) => {
                if (r) {
                  return {
                    ...r,
                    data: { ...r.data, hidden },
                  };
                }
              });
              toast.success(`Contract is now ${hidden ? "hidden" : "visible"}`);
              refetch();
            },
            onSettled() {
              confirmationModal.onClose();
            },
          },
        );
      },
    });
  };

  const onChangeFrozen = (
    freeze_until: Dayjs | null,
    body: Parameters<NonNullable<ComponentProps<typeof FreezeContractModal>["onFreeze"]>>[1],
  ) => {
    if (!contract) {
      return;
    }
    const isFreezing = !!freeze_until;
    confirmationModal.onOpen({
      title: isFreezing ? "Freeze this contract?" : "Unfreeze this contract?",
      acceptLabel: "Confirm",
      body,
      onConfirm: () => {
        changeFrozenStatus(
          {
            user_email: contract.consumer_email,
            contract_shared_uuid: contract.shared_uuid,
            freeze_until: freeze_until?.toISOString() || null,
          },
          {
            onSuccess() {
              queryClient.setQueryData<string | null>(
                frozenQueryKey,
                freeze_until?.toISOString() || null,
              );
              toast.success(`Contract is now ${isFreezing ? "frozen" : "unfrozen"}`);
              freezeModal.onClose();
              refetchFrozen();
            },
            onSettled() {
              confirmationModal.onClose();
            },
          },
        );
      },
    });
  };

  const onDelete = () => {
    if (!contract) {
      return;
    }
    confirmationModal.onOpen({
      title: "Delete this contract?",
      body: "This action cannot be undone",
      acceptLabel: "Confirm",
      onConfirm: () => {
        deleteContract(
          {
            contract_shared_uuid: contract.shared_uuid,
          },
          {
            onSuccess() {
              queryClient.invalidateQueries({ queryKey: ["contracts"] });
              toast.success("Contract was deleted, redirecting...");
              setTimeout(() => {
                queryClient.resetQueries({ queryKey });
                navigate(CONTRACTS_PATH);
              }, 1500);
            },
            onSettled() {
              confirmationModal.onClose();
            },
          },
        );
      },
    });
  };

  return (
    <>
      <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
        {contract &&
          id === contract.shared_uuid &&
          self?.is_synota_admin &&
          !self.is_synota_readonly_admin && (
            <LastEnergyDateEditor
              key={contract.shared_uuid}
              contract={contract}
              disabled={isFetching}
            />
          )}
        <Tooltip
          title={
            contract?.hide_miner_management == null
              ? "Consumer dashboard preference is unavailable"
              : "Existing integrations continue running"
          }
        >
          <span>
            <FormControlLabel
              label="Hide miner management on consumer dashboard"
              control={
                <Switch
                  checked={contract?.hide_miner_management === true}
                  disabled={
                    isFetching ||
                    isPendingVisibility ||
                    !self?.is_synota_admin ||
                    self.is_synota_readonly_admin ||
                    contract?.hide_miner_management == null
                  }
                  onChange={(_, hide_miner_management) => {
                    if (!contract) return;
                    setVisibility(
                      { contract_shared_uuid: contract.shared_uuid, hide_miner_management },
                      {
                        onSuccess() {
                          queryClient.setQueryData<AxiosResponse<IContractDetailsResponse>>(
                            queryKey,
                            (response) =>
                              response
                                ? { ...response, data: { ...response.data, hide_miner_management } }
                                : response,
                          );
                          toast.success("Miner management visibility saved");
                          refetch();
                        },
                      },
                    );
                  }}
                />
              }
            />
          </span>
        </Tooltip>
        {contract?.hidden ? (
          <Badge color="neutral" badgeContent="Hidden">
            <IconButton
              disabled={isFetching || isPendingHide}
              onClick={() => onChangeHidden(false)}
              title="Unhide contract"
            >
              <VisibilityOff />
            </IconButton>
          </Badge>
        ) : (
          <IconButton
            disabled={isFetching || isPendingHide}
            onClick={() => onChangeHidden(true)}
            title="Hide contract"
          >
            <Visibility />
          </IconButton>
        )}
        <Badge color="info" invisible={!isFrozen} badgeContent="Frozen">
          <IconButton
            color={isFrozen ? "info" : "default"}
            disabled={isFetching || isFetchingFrozen || isPendingFreeze}
            onClick={() => freezeModal.onOpen()}
            title={isFrozen ? "Unfreeze payments" : "Freeze payments"}
          >
            <AcUnit />
          </IconButton>
        </Badge>
        {contract?.contract_state === EContractState.Unsigned && (
          <IconButton
            color="error"
            disabled={isFetching || isPendingDelete}
            onClick={() => onDelete()}
            title="Delete contract"
          >
            <DeleteForever />
          </IconButton>
        )}
      </Stack>
      <ConfirmationModal {...confirmationModal} />
      {contract && freezeModal.open && (
        <FreezeContractModal
          frozenUntil={frozenUntil}
          contract={contract}
          onFreeze={onChangeFrozen}
          {...freezeModal}
        />
      )}
    </>
  );
};
